import React from 'react';
import { CalendarDaysIcon, BuildingOfficeIcon } from '@heroicons/react/16/solid';
import TravelLogo from '../Image/travel/travelLogo.svg';
import LeadLogo from '../Image/lead/leadLogo.svg';
import DocRendrLogo from '../Image/docRendr/docRendrLogo.svg';
import { BRAND_NAME, getBrandLogoSrc } from '../utils/brandAssets';

const APP_URLS = {
    appointments: process.env.REACT_APP_URL_APPOINTMENTS || '',
    bookings:     process.env.REACT_APP_URL_BOOKINGS     || '',
    travel:       process.env.REACT_APP_URL_TRAVEL       || '',
    leads:        process.env.REACT_APP_URL_LEADS        || '',
    docs:         process.env.REACT_APP_URL_DOCS         || '',
};

export default function ApplicationDashboard() {
    const [search, setSearch] = React.useState("");

    const apps = [
        {
            name: 'Appointments',
            description: 'Manage appointments and schedules within WhatsApp.',
            route: APP_URLS.appointments,
            color: 'bg-emerald-500',
            icon: <CalendarDaysIcon className="w-8 h-8 text-white" />,
        },
        {
            name: 'Bookings',
            description: 'Hotel and resource booking system within WhatsApp.',
            route: APP_URLS.bookings,
            color: 'bg-pink-500',
            icon: <BuildingOfficeIcon className="w-8 h-8 text-white" />,
        },
        {
            name: 'Travel',
            description: 'Travel planning and booking.',
            route: APP_URLS.travel,
            color: 'bg-blue-500',
            icon: <img src={TravelLogo} alt="Travel" className="w-16 h-16" />,
        },
        {
            name: 'Lead Management',
            description: 'Manage and track leads across your sales pipeline.',
            route: APP_URLS.leads,
            color: 'bg-amber-500',
            icon: <img src={LeadLogo} alt="Lead Management" className="w-16 h-16" />,
        },
        {
            name: 'Document Creator',
            description: 'Design and generate media templates and PDF documents.',
            route: APP_URLS.docs,
            color: 'bg-violet-500',
            icon: <img src={DocRendrLogo} alt="Document Creator" className="w-16 h-16" />,
        },
    ].filter(app => app.route);

    // Directly use app.route for links
    const getAppRoute = (app) => app.route;

    const filteredApps = apps.filter(app =>
        app.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Modern Header */}
            <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {/* Logo / Title */}
                        <div className="flex items-center gap-3">
                            <div className="bg-white p-2 rounded-full shadow-sm relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-300/30 to-transparent w-full h-full animate-shine" />
                                <img
                                    src={getBrandLogoSrc()}
                                    alt={`${BRAND_NAME} Logo`}
                                    className="w-8 h-8 relative z-10"
                                    referrerPolicy="no-referrer"
                                />
                            </div>
                            <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
                                Application Hub
                            </h1>
                        </div>

                        {/* Search Bar */}
                        <div className="w-full max-w-md relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                    <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                placeholder="Search apps..."
                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl leading-5 bg-gray-50 text-gray-900 placeholder-gray-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition duration-200 ease-in-out sm:text-sm shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filteredApps.map(app => (
                        <a
                            key={app.name}
                            href={getAppRoute(app)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group cursor-pointer bg-white rounded-2xl shadow-sm hover:shadow-xl border border-gray-100 p-6 flex flex-col items-center transition-all duration-300 hover:-translate-y-1 text-black no-underline"
                        >
                            <div className="mb-5 relative w-16 h-16 flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                                {/* Gradient background behind icon, color based on app */}
                                {app.name === 'Appointments' && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 rounded-2xl shadow-lg opacity-90 group-hover:opacity-100"></div>
                                )}
                                {app.name === 'Bookings' && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 rounded-2xl shadow-lg opacity-90 group-hover:opacity-100"></div>
                                )}
                                {app.name === 'Travel' && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-blue-500 to-blue-700 rounded-2xl shadow-lg opacity-90 group-hover:opacity-100"></div>
                                )}
                                {app.name === 'Lead Management' && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-amber-400 via-orange-500 to-red-500 rounded-2xl shadow-lg opacity-90 group-hover:opacity-100"></div>
                                )}
                                {app.name === 'Document Creator' && (
                                    <div className="absolute inset-0 bg-gradient-to-br from-violet-400 via-purple-500 to-indigo-600 rounded-2xl shadow-lg opacity-90 group-hover:opacity-100"></div>
                                )}
                                <span className="relative z-10 flex items-center justify-center w-full h-full transform transition-transform">{app.icon}</span>
                            </div>
                            <h2 className="text-xl font-bold mb-2 text-gray-800 transition-colors">{app.name}</h2>
                            <p className="text-center text-sm text-gray-500 leading-relaxed">{app.description}</p>
                        </a>
                    ))}
                </div>
            </main>
        </div>
    );
}
